import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ImageWithFallback from './ImageWithFallback';
import OnlineDot, { PeerStatus } from './OnlineDot';
import { FriendUser, listFriends } from '../utils/secureFunctions';
import { getProfileUrl } from '../utils/urls';

/** The signed-in user's friends, with search and quick message/profile actions. */
const FriendsList: React.FC<{ onCount?: (n: number) => void }> = ({ onCount }) => {
  const navigate = useNavigate();
  const [friends, setFriends] = useState<FriendUser[] | null>(null);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let alive = true;
    listFriends()
      .then((res) => {
        if (!alive) return;
        const list = [...(res.friends || [])].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        setFriends(list);
        onCount?.(list.length);
      })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [onCount]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!friends) return [];
    if (!q) return friends;
    return friends.filter((f) => String(f.name || '').toLowerCase().includes(q) || String(f.username || '').toLowerCase().includes(q));
  }, [friends, query]);

  if (error) return <div style={s.card}><p style={s.muted}>Could not load your friends. Please try again.</p></div>;
  if (!friends) return <div style={s.card}><p style={s.muted}>Loading friends…</p></div>;

  return (
    <div style={s.card}>
      {friends.length === 0 ? (
        <p style={s.muted}>No friends yet. Visit someone's profile and tap “Add friend”.</p>
      ) : (
        <>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${friends.length} friend${friends.length === 1 ? '' : 's'}`}
            aria-label="Search friends"
            style={s.search}
          />
          {shown.length === 0 && <p style={s.muted}>No friends match “{query}”.</p>}
          {shown.map((f) => {
            const profileUrl = getProfileUrl({ id: f.uid, username: f.username || undefined });
            return (
              <div key={f.uid} style={s.row}>
                <button type="button" style={s.person} onClick={() => navigate(profileUrl)}>
                  <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                    <ImageWithFallback src={f.photoURL || ''} alt="" style={s.avatar} />
                    <OnlineDot userId={f.uid} />
                  </span>
                  <span style={s.info}>
                    <strong style={s.name}>{f.name || 'Belivah member'}</strong>
                    {f.username && <span style={s.muted}>@{f.username}</span>}
                    <PeerStatus userId={f.uid} />
                  </span>
                </button>
                <button type="button" style={s.msgBtn} onClick={() => navigate(`/messages?user=${encodeURIComponent(f.uid)}`)}>
                  Message
                </button>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  card: { background: 'var(--color-surface, #fff)', borderRadius: 12, padding: 16, marginTop: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  muted: { color: 'var(--color-text-secondary, #6b7280)', fontSize: 13, margin: 0 },
  search: { width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 999, border: '1px solid rgba(0,0,0,0.12)', fontSize: 15, marginBottom: 8, background: 'var(--color-background, #fff)', color: 'inherit' },
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' },
  person: { display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', color: 'inherit' },
  avatar: { width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', background: '#e8f7f4' },
  info: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  name: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  msgBtn: { flexShrink: 0, padding: '8px 14px', borderRadius: 999, border: 'none', background: '#0F6C6E', color: '#fff', fontWeight: 600, cursor: 'pointer' },
};

export default FriendsList;
